import { GroupSessionOccurrence } from '@/types/treatmentGroup';
import { addDaysToDateString, dateStringInZone, formatMonthDay, parseIsoDate } from '@/utils/dates';

export interface OccurrenceWeekGroup {
  label: string;
  rangeLabel: string;
  weeksOut: number;
  occurrences: GroupSessionOccurrence[];
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

const weekLabelFor = (weeksOut: number): string => {
  if (weeksOut === 0) return 'This week';
  if (weeksOut === 1) return 'Next week';
  return `In ${weeksOut} weeks`;
};

const mondayOfDateString = (dateStr: string): string => {
  const daysSinceMonday = (parseIsoDate(dateStr).getDay() + 6) % 7;
  return addDaysToDateString(dateStr, -daysSinceMonday);
};

const rangeLabelFor = (mondayDateStr: string): string => {
  const sundayDateStr = addDaysToDateString(mondayDateStr, 6);
  return `${formatMonthDay(`${mondayDateStr}T00:00:00`)} – ${formatMonthDay(`${sundayDateStr}T00:00:00`)}`;
};

const effectiveStartAt = (occurrence: GroupSessionOccurrence): string =>
  occurrence.start_at ?? occurrence.intended_start_at;

// Groups occurrences into calendar weeks (Monday-Sunday) as they fall in
// timeZone, labeling each by how far out it is from the current week ("This
// week", "Next week", "In N weeks"), alongside its own date range.
export const groupOccurrencesByWeek = (
  occurrences: GroupSessionOccurrence[],
  timeZone: string
): OccurrenceWeekGroup[] => {
  const thisMonday = mondayOfDateString(dateStringInZone(new Date().toISOString(), timeZone));
  const thisMondayTime = parseIsoDate(thisMonday).getTime();

  const byMonday = new Map<string, GroupSessionOccurrence[]>();
  occurrences.forEach((occurrence) => {
    const monday = mondayOfDateString(dateStringInZone(effectiveStartAt(occurrence), timeZone));
    const existing = byMonday.get(monday) ?? [];
    existing.push(occurrence);
    byMonday.set(monday, existing);
  });

  return Array.from(byMonday.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([mondayDateStr, groupOccurrences]) => {
      const weeksOut = Math.round(
        (parseIsoDate(mondayDateStr).getTime() - thisMondayTime) / MS_PER_WEEK
      );

      return {
        label: weekLabelFor(weeksOut),
        rangeLabel: rangeLabelFor(mondayDateStr),
        weeksOut,
        occurrences: [...groupOccurrences].sort(
          (a, b) =>
            new Date(effectiveStartAt(a)).getTime() - new Date(effectiveStartAt(b)).getTime()
        )
      };
    });
};
